import React from 'react';
import industryRPE from '../data/industryRPE';

/**
 * RPE Graph Component
 * Displays a visual representation of Revenue Per Employee (RPE) with industry benchmarks
 * 
 * @param {Object} props - Component props
 * @param {number} props.currentRPE - The calculated RPE (revenue / employee count)
 * @param {string} props.industryId - The ID of the selected industry
 * @param {string} props.locale - The current locale (e.g., 'en-US', 'pt-BR', 'es-419')
 * @param {string} props.currency - The currently selected currency code (e.g., 'USD', 'BRL')
 * @param {Function} props.formatCurrency - Function to format currency based on locale and currency
 * @param {number} props.employeeCount - Current employee headcount
 * @param {number} props.annualRevenue - Current annual revenue
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} The RPE graph component
 */
const RPEGraph = ({ currentRPE, industryId, locale, currency, formatCurrency, employeeCount, annualRevenue, t }) => {
  // Find the industry RPE data
  const getIndustryRPE = () => {
    if (!industryId) return null;
    
    // Loop through each category to find the industry
    for (const category in industryRPE) {
      const industries = industryRPE[category]?.industries || [];
      const industry = industries.find(i => i.id === industryId);
      
      if (industry) {
        // Determine which locale to use
        let rpeLow, rpeHigh;
        if (locale === 'pt-BR') {
          rpeLow = industry.BRrpeLow;
          rpeHigh = industry.BRrpeHigh;
        } else if (locale === 'es-419') {
          rpeLow = industry.LArpeLow;
          rpeHigh = industry.LArpeHigh;
        } else { // Default to US
          rpeLow = industry.USrpeLow;
          rpeHigh = industry.USrpeHigh;
        }
        
        return { rpeLow, rpeHigh };
      }
    }
    
    return null;
  };
  
  const rpeData = getIndustryRPE();
  
  // Debug logging
  console.log('RPEGraph props:', { currentRPE, industryId, locale });
  console.log('RPE Data:', rpeData);
  
  if (!rpeData) {
    console.warn('No RPE data found for industry:', industryId);
    return <div className="text-sm text-gray-500">No industry data available for the selected industry.</div>;
  }
  
  // Ensure we have valid numbers
  if (isNaN(currentRPE) || isNaN(rpeData.rpeLow) || isNaN(rpeData.rpeHigh)) {
    console.error('Invalid RPE values:', { currentRPE, rpeLow: rpeData.rpeLow, rpeHigh: rpeData.rpeHigh });
    return <div className="text-sm text-red-500">Invalid data for RPE calculation.</div>;
  }
  
  // Calculate the maximum RPE for scaling (add 20% padding)
  const maxRPE = Math.max(rpeData.rpeHigh, currentRPE) * 1.2;
  
  // Ensure we have valid values for the graph
  if (isNaN(currentRPE) || isNaN(rpeData.rpeLow) || isNaN(rpeData.rpeHigh)) {
    return (
      <div className="text-sm text-red-500 p-4 bg-red-50 rounded-md">
        {t('errorInvalidData')}
      </div>
    );
  }
  
  console.log('RPE Values:', { currentRPE, rpeLow: rpeData.rpeLow, rpeHigh: rpeData.rpeHigh, maxRPE });
  
  // Calculate the range for the graph
  const minRPE = Math.min(rpeData.rpeLow, currentRPE, rpeData.rpeHigh);
  const range = maxRPE - minRPE;
  
  // Calculate positions as percentages
  const lowPos = ((rpeData.rpeLow - minRPE) / range) * 100;
  const currentPos = ((currentRPE - minRPE) / range) * 100;
  const highPos = ((rpeData.rpeHigh - minRPE) / range) * 100;
  
  // Determine the order of the labels
  const labels = [
    { type: 'low', value: rpeData.rpeLow, pos: lowPos, className: 'text-gray-600' },
    { type: 'current', value: currentRPE, pos: currentPos, className: 'font-bold text-blue-600' },
    { type: 'high', value: rpeData.rpeHigh, pos: highPos, className: 'text-gray-600' }
  ];
  
  // Sort by value to ensure proper ordering
  labels.sort((a, b) => a.value - b.value);
  
  // Determine the min and max values for the full range
  const minValue = 0; // Always start from 0
  const maxValue = Math.max(rpeData.rpeHigh, currentRPE, rpeData.rpeLow) * 1.1; // Add 10% padding to the max value
  
  // Calculate position as a percentage of the full range
  const calculatePosition = (value) => {
    return ((value - minValue) / (maxValue - minValue)) * 100;
  };

  // Format a value for display on the graph
  const formatValue = (value) => {
    if (value >= 1000000) {
      return formatCurrency(value / 1000000) + 'M';
    } else if (value >= 1000) {
      return formatCurrency(value / 1000) + 'K';
    }
    return formatCurrency(value);
  };
  
  //
  
  // Format multiplier with exactly one decimal place and correct decimal separator
  const formatMultiplier = (value) => {
    // Prefer Intl with the provided locale
    const formatter = new Intl.NumberFormat(locale || 'en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      useGrouping: false
    });
    let out = formatter.format(value);
    // Harden for Portuguese locales: ensure comma as decimal separator
    if (typeof locale === 'string' && locale.toLowerCase().startsWith('pt')) {
      // Only swap the decimal separator if a dot exists (no grouping used)
      out = out.replace('.', ',');
    }
    return out;
  };

  // Create an array of all RPE points (low, current, high) with their positions and labels
  const rpePoints = [
    { 
      type: 'low',
      value: rpeData.rpeLow,
      label: t('lowRPE'),
      color: '#ef4444',
      position: calculatePosition(rpeData.rpeLow)
    },
    { 
      type: 'current',
      value: currentRPE,
      label: t('yourRPE'),
      color: '#3b82f6',
      position: calculatePosition(currentRPE)
    },
    { 
      type: 'high',
      value: rpeData.rpeHigh,
      label: t('highRPE'),
      color: '#22c55e',
      position: calculatePosition(rpeData.rpeHigh)
    }
  ];
  
  // Sort points by value to ensure proper ordering
  rpePoints.sort((a, b) => a.value - b.value);
  
  //

  return (
    <div className="mt-6">
      {/* Bar Graph */}
      <div className="h-8 w-full rounded-md overflow-hidden relative bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 mb-2">
        {/* Marker lines */}
        {rpePoints.map((point, index) => {
          const isFirst = index === 0;
          const isLast = index === rpePoints.length - 1;
          let markerPosition = point.position;
          
          // Position first and last markers 2% in from the edges
          if (isFirst) markerPosition = 2; // 2% from left
          if (isLast) markerPosition = 98; // 2% from right
          
          return (
            <div 
              key={`${point.type}-marker`}
              className={`absolute top-0 bottom-0 w-0.5 ${
                point.type === 'current' ? 'bg-blue-600' : 'bg-gray-900'
              }`}
              style={{
                left: `${markerPosition}%`,
                transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(0)' : 'translateX(-50%)',
                height: '100%'
              }}
            />
          );
        })}
      </div>
      
      {/* RPE Points */}
      <div className="relative h-12 px-2">
        {rpePoints.map((point, index) => {
          // Calculate if this is the leftmost or rightmost point
          const isFirst = index === 0;
          const isLast = index === rpePoints.length - 1;
          
          // Position the first point at the very left, last at the very right
          if (isFirst) {
            point.position = 0;
          } else if (isLast) {
            point.position = 100;
          }
          
          // Add some padding to prevent text from touching the edges
          const paddingStyle = {};
          if (isFirst) {
            paddingStyle.left = '0';
            paddingStyle.transform = 'translateX(0)';
          } else if (isLast) {
            paddingStyle.right = '0';
            paddingStyle.left = 'auto';
            paddingStyle.transform = 'translateX(0)';
          } else {
            paddingStyle.left = `${point.position}%`;
            paddingStyle.transform = 'translateX(-50%)';
          }
          
          return (
            <div 
              key={point.type}
              className="absolute bottom-0 text-center"
              style={{
                left: isLast ? 'auto' : isFirst ? '0' : `${point.position}%`,
                right: isLast ? '0' : 'auto',
                ...paddingStyle,
                minWidth: 'max-content',
                maxWidth: '30%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              <div className="text-sm font-medium text-gray-900">
                {formatValue(point.value)}
              </div>
              <div 
                className={`text-sm font-medium mt-1 ${
                  point.type === 'current' ? 'text-blue-600' : 'text-gray-700'
                }`}
              >
                {point.label}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Industry Comparison Section */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          {t('industryComparison')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Box 1 */}
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <h4 className="text-sm font-medium text-gray-500">
              {t('currentRPEscore')}
            </h4>
            {rpeData && (
              <p 
                className={`text-2xl font-bold mt-1 ${
                  currentRPE < rpeData.rpeLow ? 'text-red-600' : 
                  currentRPE > rpeData.rpeHigh ? 'text-green-600' : 'text-gray-900'
                }`}
              >
                {(() => {
                  const range = rpeData.rpeHigh - rpeData.rpeLow;
                  const position = (currentRPE - rpeData.rpeLow) / range;
                  
                  // Below range
                  if (currentRPE < rpeData.rpeLow) {
                    const multiplierValue = rpeData.rpeLow / currentRPE;
                    const formattedMultiplier = formatMultiplier(multiplierValue);
                    return t('rpeBelowRange').replace('{multiplier}', formattedMultiplier);
                  }
                  
                  // Above range
                  if (currentRPE > rpeData.rpeHigh) {
                    const multiplierValue = currentRPE / rpeData.rpeHigh;
                    const formattedMultiplier = formatMultiplier(multiplierValue);
                    return t('rpeAboveRange').replace('{multiplier}', formattedMultiplier);
                  }
                  
                  // Within range - show percentile
                  if (position < 0.25) return t('rpeBottom25');
                  if (position > 0.75) return t('rpeTop25');
                  return t('rpeAverage');
                })()}
              </p>
            )}
          </div>
          
          {/* Box 2 */}
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <h4 className="text-sm font-medium text-gray-500">
              {t('industryRPEAverage')}
            </h4>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {rpeData ? formatCurrency((rpeData.rpeLow + rpeData.rpeHigh) / 2) : 'N/A'}
            </p>
          </div>
          
          
        </div>

        {/* Additional insights row: show only if not Top 25% and not above range */}
        {(() => {
          if (!rpeData) return null;
          const baseRange = rpeData.rpeHigh - rpeData.rpeLow;
          if (!(isFinite(baseRange) && baseRange > 0)) return null;
          if (!isFinite(currentRPE)) return null;
          const isAboveRange = currentRPE > rpeData.rpeHigh;
          const rel = (currentRPE - rpeData.rpeLow) / baseRange;
          const shouldShowGaps = !isAboveRange && rel < 0.25; // strictly below Average
          if (!shouldShowGaps) return null;

          const industryAverage = (rpeData.rpeLow + rpeData.rpeHigh) / 2;
          // Conditional Revenue Shortfall and heading
          let shortfallBaseline = rpeData.rpeLow; // default to minimum
          let shortfallTitleKey = 'RPEshortfallToMin';
          if (currentRPE >= rpeData.rpeLow && currentRPE < industryAverage) {
            shortfallBaseline = industryAverage;
            shortfallTitleKey = 'RPEshortfallToAvg';
          }
          const revenueShortfall = (isFinite(employeeCount) && isFinite(annualRevenue))
            ? Math.max(0, (employeeCount * shortfallBaseline) - annualRevenue)
            : 0;
          //
          // Employee overstaff delta based on baseline RPE (low or average)
          const targetEmployees = (isFinite(annualRevenue) && isFinite(shortfallBaseline) && shortfallBaseline > 0)
            ? (annualRevenue / shortfallBaseline)
            : NaN;
          const employeeOverstaff = (isFinite(employeeCount) && isFinite(targetEmployees))
            ? Math.max(0, employeeCount - targetEmployees)
            : 0;
          const employeeOverstaffRounded = Math.round(employeeOverstaff);

          // Overstaff title mirrors the selected baseline
          const overstaffTitleKey = shortfallTitleKey === 'RPEshortfallToMin' ? 'RPEoverStaffToMin' : 'RPEoverStaffToAvg';

          return (
            <>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h4 className="text-sm font-medium text-gray-500">
                  {t(shortfallTitleKey, shortfallTitleKey === 'RPEshortfallToMin' ? 'Revenue Shortfall to Industry Low' : 'Revenue Shortfall to Industry Average')}
                </h4>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(revenueShortfall)}
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h4 className="text-sm font-medium text-gray-500">
                  {t(overstaffTitleKey, overstaffTitleKey === 'RPEoverStaffToMin' ? 'Overstaff to Industry Low' : 'Overstaff to Industry Average')}
                </h4>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(employeeOverstaffRounded)}
                </p>
              </div>
            </div>
            <div className="mt-2">
              <p className="text-base md:text-lg font-medium text-red-600">
                {t('RPEbelowAverageNote', 'AI automation can have significantly more impact on companies that have below-average Revenue Per Employee figures. Employee productivity gains can be disproportionate in this situation.')}
              </p>
            </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default RPEGraph;
